import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import {
  isEmbeddedInSucceed,
  getHighschoolId,
  getEmbeddedNavianceHighschoolIdHeader,
  isQaSucceedApiProxyDev,
} from '../utils/cp-context';

let apiClientInstance: AxiosInstance | undefined;

/**
 * Configuration options for the API client
 */
export interface ApiClientConfig {
  baseURL?: string;
  getAuthToken?: () => string | null;
}

/**
 * Creates and configures the axios instance for API requests.
 * Uses singleton pattern to ensure consistent configuration.
 * When embedded in Succeed, sets withCredentials: true so cookies (SUCCEED_ACCESS)
 * are sent with cross-origin requests to succeed-api.
 */
export function createApiClient(config: ApiClientConfig = {}): AxiosInstance {
  if (apiClientInstance) {
    return apiClientInstance;
  }

  const { baseURL = '/api', getAuthToken } = config;

  apiClientInstance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 30000,
    // When embedded in Succeed, requests go cross-origin (e.g. tf-succeed-56-qa.mango.naviance.com
    // -> succeed-api.qa.staff.naviance.com). Cookies are not sent by default; withCredentials
    // ensures SUCCEED_ACCESS and other auth cookies are included.
    withCredentials: isEmbeddedInSucceed(),
  });

  // Request interceptor for auth and headers
  apiClientInstance.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig) => {
      if (getAuthToken) {
        const token = getAuthToken();
        if (token) {
          requestConfig.headers.Authorization = `Bearer ${token}`;
        }
      }

      // Add headers required by course-planner-service for local development
      // In production, these are added by succeed-api gateway
      const isQaStudentDev =
        isQaSucceedApiProxyDev() && import.meta.env.VITE_APP_TARGET === 'student';
      if (!isEmbeddedInSucceed() && !isQaStudentDev) {
        const highschoolId = getHighschoolId();
        if (highschoolId) {
          requestConfig.headers['naviance-highschool-id'] = highschoolId;
          // x-user-authorized-schools is required along with naviance-highschool-id
          // For local dev, we authorize the configured school
          requestConfig.headers['x-user-authorized-schools'] = highschoolId;
        }
      }

      // CPUI parity: always send naviance-highschool-id when embedded (empty string for district staff).
      if (isEmbeddedInSucceed()) {
        requestConfig.headers['naviance-highschool-id'] = getEmbeddedNavianceHighschoolIdHeader();
      }

      return requestConfig;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor for error handling
  apiClientInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Handle 401 Unauthorized
      if (error.response?.status === 401) {
        // Redirect to login or refresh token
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }

      // Handle 403 Forbidden
      if (error.response?.status === 403) {
        window.dispatchEvent(new CustomEvent('auth:forbidden'));
      }

      return Promise.reject(error);
    }
  );

  return apiClientInstance;
}

/**
 * Reset the API client instance (useful for testing)
 */
export function resetApiClient(): void {
  apiClientInstance = undefined;
}
